"use client"

import { useEffect, useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Newspaper, Megaphone, Wrench, Tag } from "@/lib/icons"
import { useAuthStore } from "@/lib/auth-store"
import { useTranslations } from "next-intl"
import { useModalSlot } from "@/lib/client/modal-coordinator"
import { LOCAL_FIRST_SEEN_DATE_KEY } from "@/components/modals/early-access-modal"

const ONE_DAY_MS = 24 * 60 * 60 * 1000

interface PatchNote {
  id: string
  version: string
  title: string
  content: string
  release_date: string
  hero_image_url: string | null
  hero_media_type: "image" | "gif" | "video"
  background_gradient: string | null
  background_image_url: string | null
  content_top_offset: number
  announcement_type: "patch_note" | "maintenance" | "announcement" | "deal"
}

// This modal is the app-wide broadcast mechanism, not just release notes —
// it has NO premium/consent gating anywhere in this file (confirmed
// deliberately, not an oversight), so it already reaches every visitor
// regardless of ad-free status. announcement_type distinguishes what kind of
// broadcast this is; the header/icon below matches that instead of always
// reading "What's New" for a maintenance notice or a limited-time deal.
const TYPE_META = {
  patch_note: { icon: Newspaper, headingKey: "whatSNew" as const },
  announcement: { icon: Megaphone, headingKey: "announcementHeading" as const },
  maintenance: { icon: Wrench, headingKey: "maintenanceHeading" as const },
  deal: { icon: Tag, headingKey: "dealHeading" as const },
}

export function PatchNotesModal() {
  const t = useTranslations("patchNotesModal")
  const [open, setOpen] = useState(false)
  const [patchNote, setPatchNote] = useState<PatchNote | null>(null)
  const [loading, setLoading] = useState(true)
  // "Seen" version is stored on the account (profiles.last_seen_patch_version)
  // for logged-in users, so it doesn't reappear after logging into a
  // different browser/device. localStorage is only the source of truth for
  // logged-out visitors, who have no account row to persist it on.
  const { user, profile, isLoading: authLoading, sessionVerified } = useAuthStore()

  useEffect(() => {
    // Wait for auth to resolve so a logged-in user's real last-seen version
    // (from their profile) is known before deciding whether to show this —
    // otherwise a fresh browser would flash the modal before the profile loads.
    // sessionVerified specifically guards against treating a stale, merely
    // localStorage-cached `user`/`profile` (session since expired/cleared
    // without an explicit sign-out) as proof this visitor is actually signed
    // in — that would otherwise show them a version-seen state that isn't
    // really theirs. Falls back to the guest (localStorage) path instead.
    if (authLoading) return
    const isActuallySignedIn = sessionVerified && !!user

    const checkForUpdates = async () => {
      try {
        const response = await fetch("/api/patch-notes/latest")
        const { patchNote: latestNote } = await response.json()

        if (!latestNote) {
          setLoading(false)
          return
        }

        // Brand-new signups don't need to "catch up" on what's new — this was
        // clashing with the day-one promo cards (Early Access/Glymtober) on a
        // first visit. Scoped to patch_note specifically: a live maintenance
        // notice or announcement should still reach a brand-new visitor
        // immediately, only the changelog case needs to wait a day.
        if (latestNote.announcement_type === "patch_note") {
          const isBrandNew = isActuallySignedIn
            ? !!profile?.created_at && Date.now() - new Date(profile.created_at).getTime() < ONE_DAY_MS
            : (() => {
                try { return localStorage.getItem(LOCAL_FIRST_SEEN_DATE_KEY) === new Date().toISOString().slice(0, 10) } catch { return false }
              })()
          if (isBrandNew) {
            setLoading(false)
            return
          }
        }

        const lastSeenVersion = isActuallySignedIn
          ? profile?.last_seen_patch_version ?? null
          : localStorage.getItem("lastSeenPatchVersion")

        if (lastSeenVersion !== latestNote.version) {
          setPatchNote(latestNote)
          setOpen(true)
        }
      } catch (error) {
        console.error("Error checking for updates:", error)
      } finally {
        setLoading(false)
      }
    }

    const run = () => { void checkForUpdates() }
    if ("requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(run, { timeout: 3000 })
      return () => window.cancelIdleCallback(idleId)
    }
    const timeoutId = (window as Window).setTimeout(run, 1500)
    return () => window.clearTimeout(timeoutId)
  }, [authLoading, user, profile?.last_seen_patch_version])

  const handleClose = () => {
    if (patchNote) {
      if (sessionVerified && user) {
        fetch("/api/profile", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ last_seen_patch_version: patchNote.version }),
        })
          .then(() => useAuthStore.getState().refreshProfile())
          .catch(() => {})
      } else {
        localStorage.setItem("lastSeenPatchVersion", patchNote.version)
      }
    }
    setOpen(false)
  }

  const canShow = useModalSlot("patchNotes", open && !loading && !!patchNote)

  if (loading || !patchNote) {
    return null
  }

  const { icon: HeadingIcon, headingKey } = TYPE_META[patchNote.announcement_type] ?? TYPE_META.patch_note

  // background_image_url is a DIFFERENT concept from hero_image_url: the hero
  // is a cropped top-only strip (see below), this is full-bleed art behind
  // the whole dialog — title, content, everything — so a designer can make
  // one continuous piece of art that flows behind the text instead of
  // stopping at a hard crop line. Wins over background_gradient (rendered on
  // top of it) when both are set, per the admin form's own stated priority.
  const dialogStyle: React.CSSProperties | undefined = patchNote.background_image_url
    ? { backgroundImage: `url(${patchNote.background_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }
    : patchNote.background_gradient
      ? { background: patchNote.background_gradient }
      : undefined

  // A hard border-t divider makes sense against the plain default dialog
  // background, but cuts a visible line across custom art that's meant to
  // read as one continuous piece — direct feedback on a live screenshot.
  // Dropped whenever any custom art is present (hero strip OR full
  // background); the spacing (pt-4/pb-4/px-6) stays identical either way,
  // only the line itself is removed.
  const hasCustomArt = !!(patchNote.hero_image_url || patchNote.background_image_url)

  return (
    <Dialog open={open && canShow} onOpenChange={handleClose}>
      {/* p-0 + the media sitting outside DialogHeader's own padding, so hero
          art can run edge-to-edge across the top of the dialog rather than
          being boxed in by the same padding as the text below it. Falls back
          to exactly the old plain-text layout (Sparkles icon + title, no
          media, default dialog background) when hero_image_url/
          background_gradient/background_image_url are all null — that was
          the only real gap in an otherwise fully-working, never-actually-
          used feature (0 rows in production before this). background_gradient
          and background_image_url are the same house_ads.background_gradient
          convention used for the House Ad Interstitial
          (components/ads/HouseAd.tsx) and every in-grid house ad — reused
          here rather than inventing a separate "art" system. */}
      <DialogContent
        className={patchNote.hero_image_url ? "max-w-2xl max-h-[80vh] p-0 overflow-hidden" : "max-w-2xl max-h-[80vh]"}
        style={dialogStyle}
      >
        {patchNote.hero_image_url && (
          patchNote.hero_media_type === "video" ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video
              src={patchNote.hero_image_url}
              autoPlay
              muted
              loop
              playsInline
              className="w-full max-h-56 object-cover"
            />
          ) : (
            // hero_media_type "gif" also renders as a plain <img> — browsers
            // autoplay animated gifs there already, same convention as
            // house_ads' own media rendering (components/ads/HouseAd.tsx).
            <img
              src={patchNote.hero_image_url}
              alt=""
              className="w-full max-h-56 object-cover"
            />
          )
        )}
        {/* content_top_offset pushes the header (and everything after it)
            down from its default position — set by the admin to line up
            with a specific spot in custom art (e.g. a plaque/banner shape
            drawn into background_image_url), the Early Access modal's "text
            integrated into the art, not a cutoff" reference. A plain
            marginTop rather than folding into DialogHeader's own conditional
            padding above, so it's additive regardless of whether a hero
            strip is also present. 0 (the default) changes nothing. */}
        <div style={patchNote.content_top_offset ? { marginTop: patchNote.content_top_offset } : undefined}>
          <DialogHeader className={patchNote.hero_image_url ? "px-6 pt-4" : undefined}>
            <div className="flex items-center gap-2">
              <HeadingIcon className="h-5 w-5 text-primary" />
              <DialogTitle className="text-2xl">{t(headingKey)}</DialogTitle>
            </div>
            <DialogDescription>
              {/* A version number reads correctly for an actual release, but
                  not for a maintenance notice or a limited-time deal — those
                  still carry a `version` (NOT NULL, used for the per-account
                  seen-dedup key) but it's an internal id, not something to
                  surface as "Version X" to the visitor. */}
              {patchNote.announcement_type === "patch_note"
                ? t("versionNumberDate", { number: patchNote.version, date: new Date(patchNote.release_date).toLocaleDateString() })
                : t("postedOnDate", { date: new Date(patchNote.release_date).toLocaleDateString() })}
            </DialogDescription>
          </DialogHeader>
        </div>

        <ScrollArea className={patchNote.hero_image_url ? "max-h-[50vh] px-6 pr-4" : "max-h-[50vh] pr-4"}>
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">{patchNote.title}</h3>
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <div
                dangerouslySetInnerHTML={{
                  __html: patchNote.content.replace(/\n/g, "<br />"),
                }}
              />
            </div>
          </div>
        </ScrollArea>

        <div
          className={
            patchNote.hero_image_url
              ? `flex justify-end gap-2 pt-4 pb-4 px-6 ${hasCustomArt ? "" : "border-t"}`
              : `flex justify-end gap-2 pt-4 ${hasCustomArt ? "" : "border-t"}`
          }
        >
          {/* rounded-lg to match DialogContent's own corner radius (see
              components/ui/dialog.tsx) — the Button component's default
              rounded-md is a visibly different, smaller radius, reported
              live as inconsistent with the rest of the app's styling. */}
          <Button onClick={handleClose} className="!rounded-lg">{t("gotIt")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
